const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const escapeAttribute = (value = "") => escapeHtml(value).replace(/`/g, "&#96;");

const isSafeUrl = (value = "") => /^(https?:\/\/|mailto:)/i.test(String(value).trim());

const renderInline = (value = "") => {
  let output = escapeHtml(value);

  output = output.replace(/!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]+)")?\)/g, (_m, alt, url, title) => {
    if (!isSafeUrl(url)) return escapeHtml(alt || "");
    const titleAttr = title ? ` title="${escapeAttribute(title)}"` : "";
    return `<img src="${escapeAttribute(url)}" alt="${escapeAttribute(alt)}"${titleAttr} />`;
  });

  output = output.replace(/\[([^\]]+)\]\(([^)\s]+)(?:\s+"([^"]+)")?\)/g, (_m, label, url, title) => {
    if (!isSafeUrl(url)) return label;
    const titleAttr = title ? ` title="${escapeAttribute(title)}"` : "";
    return `<a href="${escapeAttribute(url)}" target="_blank" rel="noreferrer"${titleAttr}>${label}</a>`;
  });

  output = output.replace(/`([^`]+)`/g, "<code>$1</code>");
  output = output.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  output = output.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  output = output.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, "$1<em>$2</em>");
  output = output.replace(/(^|[^_])_([^_\n]+)_(?!_)/g, "$1<em>$2</em>");
  output = output.replace(/~~([^~]+)~~/g, "<del>$1</del>");

  return output;
};

const flushList = (listType, listItems) => {
  if (!listType || !listItems.length) return "";
  return `<${listType}>${listItems.map((item) => `<li>${renderInline(item)}</li>`).join("")}</${listType}>`;
};

export const markdownToHtml = (source = "") => {
  const normalized = String(source || "").replace(/\r\n?/g, "\n").trim();
  if (!normalized) return "";

  const lines = normalized.split("\n");
  const html = [];
  let paragraphLines = [];
  let listType = "";
  let listItems = [];
  let codeFence = false;
  let codeLines = [];

  const flushParagraph = () => {
    if (!paragraphLines.length) return;
    html.push(`<p>${renderInline(paragraphLines.join(" "))}</p>`);
    paragraphLines = [];
  };

  const flushCurrentList = () => {
    const markup = flushList(listType, listItems);
    if (markup) html.push(markup);
    listType = "";
    listItems = [];
  };

  const flushCodeBlock = () => {
    if (!codeFence) return;
    html.push(`<pre><code>${escapeHtml(codeLines.join("\n"))}</code></pre>`);
    codeFence = false;
    codeLines = [];
  };

  for (const line of lines) {
    if (/^```/.test(line.trim())) {
      flushParagraph();
      flushCurrentList();
      if (codeFence) {
        flushCodeBlock();
      } else {
        codeFence = true;
        codeLines = [];
      }
      continue;
    }

    if (codeFence) {
      codeLines.push(line);
      continue;
    }

    const trimmed = line.trim();

    if (!trimmed) {
      flushParagraph();
      flushCurrentList();
      continue;
    }

    if (/^---+$|^\*\*\*+$/.test(trimmed)) {
      flushParagraph();
      flushCurrentList();
      html.push("<hr />");
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      flushParagraph();
      flushCurrentList();
      const level = headingMatch[1].length;
      html.push(`<h${level}>${renderInline(headingMatch[2])}</h${level}>`);
      continue;
    }

    const blockquoteMatch = trimmed.match(/^>\s?(.*)$/);
    if (blockquoteMatch) {
      flushParagraph();
      flushCurrentList();
      html.push(`<blockquote><p>${renderInline(blockquoteMatch[1])}</p></blockquote>`);
      continue;
    }

    const unorderedMatch = trimmed.match(/^[-*]\s+(.*)$/);
    if (unorderedMatch) {
      flushParagraph();
      if (listType && listType !== "ul") flushCurrentList();
      listType = "ul";
      listItems.push(unorderedMatch[1]);
      continue;
    }

    const orderedMatch = trimmed.match(/^\d+\.\s+(.*)$/);
    if (orderedMatch) {
      flushParagraph();
      if (listType && listType !== "ol") flushCurrentList();
      listType = "ol";
      listItems.push(orderedMatch[1]);
      continue;
    }

    flushCurrentList();
    paragraphLines.push(trimmed);
  }

  flushParagraph();
  flushCurrentList();
  flushCodeBlock();

  return html.join("");
};

export const stripMarkdown = (source = "") =>
  String(source || "")
    .replace(/\r\n?/g, "\n")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, "$1")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1")
    .replace(/[`>#*_~-]+/g, " ")
    .replace(/\d+\.\s+/g, " ")
    .replace(/\n+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
